import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { guardiansApi } from '@/services/api/endpoints/guardians';
import { queryKeys } from '@/services/api/queryKeys';
import type { LinkGuardianPayload } from '@/services/api/endpoints/guardians';
import type { RelationshipType, StudentGuardian } from '@/types/domain';

const RELATIONSHIPS: RelationshipType[] = [
  'MOTHER',
  'FATHER',
  'GUARDIAN',
  'GRANDPARENT',
  'SIBLING',
  'UNCLE',
  'AUNT',
  'OTHER',
];

/** What a guardian may do for a child, each independently grantable. */
const AUTHORISATIONS = [
  {
    key: 'emergencyContact' as const,
    label: 'Emergency contact',
    hint: 'Called first if the child is hurt or unwell',
  },
  {
    key: 'pickupAuthorization' as const,
    label: 'May collect the child',
    hint: 'Permitted to take the child from school',
  },
  {
    key: 'financialResponsibility' as const,
    label: 'Receives invoices',
    hint: 'Fees and statements are addressed here',
  },
  {
    key: 'livesWithStudent' as const,
    label: 'Lives with the child',
    hint: 'Shares the child’s home address',
  },
];

interface Props {
  open: boolean;
  /** Supplied when editing an existing link rather than creating one. */
  link?: StudentGuardian | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: LinkGuardianPayload) => void;
}

/**
 * Links an existing guardian to a child.
 *
 * There is deliberately no "create guardian" shortcut here. A parent registering
 * a second child is the common case, and a shortcut would produce a duplicate
 * person whose details then drift from the first — so the guardian is chosen
 * from those already recorded.
 */
export function LinkGuardianModal({ open, link = null, saving, onClose, onSubmit }: Props) {
  const editing = link !== null;

  const [guardianId, setGuardianId] = useState(link?.guardianId ?? '');
  const [search, setSearch] = useState('');
  const [relationshipType, setRelationshipType] = useState<RelationshipType>(
    link?.relationshipType ?? 'MOTHER',
  );
  const [primaryGuardian, setPrimaryGuardian] = useState(link?.primaryGuardian ?? false);
  const [flags, setFlags] = useState({
    emergencyContact: link?.emergencyContact ?? true,
    pickupAuthorization: link?.pickupAuthorization ?? false,
    financialResponsibility: link?.financialResponsibility ?? false,
    livesWithStudent: link?.livesWithStudent ?? true,
  });
  const [notes, setNotes] = useState(link?.notes ?? '');

  const candidates = useQuery({
    queryKey: queryKeys.guardians.list({ search, size: 20, status: 'ACTIVE' }),
    queryFn: () => guardiansApi.list({ search, size: 20, page: 0, status: 'ACTIVE' }),
    enabled: open && !editing,
  });

  const submit = () => {
    onSubmit({
      guardianId,
      relationshipType,
      primaryGuardian,
      ...flags,
      notes: notes === '' ? undefined : notes,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edit ${link.guardianName}’s role` : 'Link a guardian'}
      description={
        editing
          ? 'Changes what this guardian may do for this child. Their own details are edited on the guardian record, since those are shared with any siblings.'
          : 'Choose someone already recorded. Adding the same person twice creates two sets of contact details that quietly diverge.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving} disabled={guardianId === ''}>
            {editing ? 'Save changes' : 'Link guardian'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!editing && (
          <>
            <TextField
              label="Find a guardian"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, phone or e-mail…"
            />
            <SelectField
              label="Guardian"
              required
              value={guardianId}
              onChange={(event) => setGuardianId(event.target.value)}
              hint={
                candidates.data?.data.length === 0
                  ? 'No matches. Add the guardian from the Guardians screen first.'
                  : undefined
              }
            >
              <option value="">Select a guardian…</option>
              {candidates.data?.data.map((guardian) => (
                <option key={guardian.id} value={guardian.id}>
                  {guardian.fullName} · {guardian.phone}
                </option>
              ))}
            </SelectField>
          </>
        )}

        <SelectField
          label="Relationship"
          required
          value={relationshipType}
          onChange={(event) => setRelationshipType(event.target.value as RelationshipType)}
        >
          {RELATIONSHIPS.map((relationship) => (
            <option key={relationship} value={relationship}>
              {relationship.charAt(0) + relationship.slice(1).toLowerCase()}
            </option>
          ))}
        </SelectField>

        <fieldset className="rounded-lg border border-line p-4">
          <legend className="px-1 text-xs font-medium text-muted">What this guardian may do</legend>

          <label className="flex items-start gap-3 py-1.5">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 border-line text-accent focus:ring-accent"              checked={primaryGuardian}
              onChange={(event) => setPrimaryGuardian(event.target.checked)}
            />
            <span>
              <span className="block text-sm text-ink">Primary guardian</span>
              <span className="block text-xs text-subtle">
                The school’s first point of contact. Naming a new one steps the current primary down.
              </span>
            </span>
          </label>

          {AUTHORISATIONS.map((authorisation) => (
            <label key={authorisation.key} className="flex items-start gap-3 py-1.5">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 border-line text-accent focus:ring-accent"                checked={flags[authorisation.key]}
                onChange={(event) =>
                  setFlags((current) => ({ ...current, [authorisation.key]: event.target.checked }))
                }
              />
              <span>
                <span className="block text-sm text-ink">{authorisation.label}</span>
                <span className="block text-xs text-subtle">{authorisation.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <TextField
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Anything the office should know"
        />
      </div>
    </Modal>
  );
}
